import { useMemo, useRef, useState } from 'react'
import { useSettings, useUpdateSettings } from '@/hooks/useSettings'
import { SettingsValidationError } from '@/lib/api/settings'
import {
  diffChanges,
  isDirty,
  normalizeCurrency,
  reconcileDraft,
  settingsToValues,
} from '@/lib/settings-form'
import type { SettingField } from '@/lib/schemas/settings'
import { TestKhButton } from '@/components/shared/TestKhButton'

export default function Settings() {
  const query = useSettings()
  const update = useUpdateSettings()
  const server = query.data

  const baseline = useMemo(
    () => (server ? settingsToValues(server.sections) : {}),
    [server],
  )
  // Seed the draft from server data already in cache so a remount (navigating
  // back to /settings) shows values immediately instead of blank inputs.
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    server ? settingsToValues(server.sections) : {},
  )
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)
  const [syncedFrom, setSyncedFrom] = useState(server)
  const justSaved = useRef(false)

  // Re-sync the draft whenever the server shape changes. After the user's own
  // save the mutation seeds the cache with the authoritative shape, so we fully
  // replace the draft; a background refetch instead MERGES, keeping any unsaved
  // edits (dirty draft wins). Adjusting state during render is React's
  // recommended pattern for "reset when data changes".
  if (server && server !== syncedFrom) {
    const nextValues = settingsToValues(server.sections)
    const wasSave = justSaved.current
    justSaved.current = false
    setDraft((current) =>
      wasSave
        ? nextValues
        : reconcileDraft(settingsToValues(syncedFrom?.sections ?? []), nextValues, current),
    )
    setSyncedFrom(server)
    setFieldErrors({})
  }

  const dirty = isDirty(baseline, draft)

  function setField(key: string, value: string) {
    setDraft((d) => ({ ...d, [key]: value }))
    setSaved(false)
    setFieldErrors((e) => {
      if (!(key in e)) return e
      const next = { ...e }
      delete next[key]
      return next
    })
  }

  function save() {
    const changes = diffChanges(baseline, draft)
    setSaved(false)
    update.mutate(changes, {
      onSuccess: () => {
        justSaved.current = true
        setFieldErrors({})
        setSaved(true)
      },
      onError: (err) => {
        if (err instanceof SettingsValidationError) setFieldErrors(err.errors)
      },
    })
  }

  const genericError =
    update.isError && !(update.error instanceof SettingsValidationError)
      ? update.error instanceof Error
        ? update.error.message
        : 'Failed to save settings.'
      : null

  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold">Settings</h1>

      <p className="text-sm text-gray-500">
        Changes take effect immediately — the app reads these values at runtime, so no redeploy is
        needed. Every change is logged in the audit trail with the old and new value.
      </p>

      {query.isLoading ? (
        <p className="text-sm text-gray-500">Loading settings…</p>
      ) : query.isError ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-red-600">
            {query.error instanceof Error ? query.error.message : 'Failed to load settings.'}
          </p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white"
          >
            Retry
          </button>
        </div>
      ) : server ? (
        <>
          {server.sections.map((section) => (
            <section key={section.title} className="space-y-4 border border-gray-200 p-4">
              <h2 className="text-base font-semibold">{section.title}</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {section.settings.map((field) => (
                  <Field
                    key={field.key}
                    field={field}
                    value={draft[field.key] ?? ''}
                    error={fieldErrors[field.key]}
                    onChange={(v) => setField(field.key, v)}
                    onBlur={() => {
                      if (field.type === 'currency') {
                        setField(field.key, normalizeCurrency(draft[field.key] ?? ''))
                      }
                    }}
                  />
                ))}
              </div>
            </section>
          ))}

          <p className="border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            KH API credentials are not stored here — they live in the server's ENV file.
          </p>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={!dirty || update.isPending}
              className="border border-gray-800 bg-gray-900 px-3 py-1 text-sm text-white hover:bg-black disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-200 disabled:text-gray-400"
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </button>
            {saved && !dirty && <span className="text-sm text-green-700">Saved.</span>}
            {genericError && (
              <span role="alert" className="text-sm text-red-600">
                {genericError}
              </span>
            )}
            {Object.keys(fieldErrors).length > 0 && (
              <span role="alert" className="text-sm text-red-600">
                Some settings are invalid — see the highlighted fields.
              </span>
            )}
          </div>

          <section className="space-y-3 border border-gray-200 p-4">
            <div>
              <h2 className="text-base font-semibold">KH API connection</h2>
              <p className="text-xs text-gray-500">
                Run this any time something seems wrong with dropship submissions, before calling
                Anthony.
              </p>
            </div>
            <TestKhButton />
          </section>
        </>
      ) : null}
    </div>
  )
}

function Field({
  field,
  value,
  error,
  onChange,
  onBlur,
}: {
  field: SettingField
  value: string
  error?: string
  onChange: (value: string) => void
  onBlur: () => void
}) {
  const id = `setting-${field.key}`
  const base = `border px-2 py-1 text-sm ${error ? 'border-red-400' : 'border-gray-300'}`

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium">
        {field.label}
      </label>
      {field.type === 'currency' ? (
        <div className="flex items-center">
          <span className="border border-r-0 border-gray-300 bg-gray-50 px-2 py-1 text-sm text-gray-500">
            $
          </span>
          <input
            id={id}
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            className={`${base} w-full tabular-nums`}
          />
        </div>
      ) : field.type === 'integer' ? (
        <input
          id={id}
          type="number"
          step="1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className={`${base} w-full tabular-nums`}
        />
      ) : (
        <input
          id={id}
          type="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className={`${base} w-full`}
        />
      )}
      {field.help && <p className="text-xs text-gray-500">{field.help}</p>}
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}
